import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '~/lib/supabase-browser';

export interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  role: 'superadmin' | 'installer';
  created_at: string;
  updated_at: string;
}

export type AAL = 'aal1' | 'aal2' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  // AAL of the current session — null while resolving / signed out.
  // 'aal1' = password-only; 'aal2' = MFA verified for this session.
  currentAAL: AAL;
  // The highest AAL this user could achieve (i.e., 'aal2' iff a verified
  // factor is enrolled). Used to decide enroll-vs-challenge in /mfa.
  nextAAL: AAL;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
  // Refreshes currentAAL/nextAAL after MFA enroll/verify so the rest of
  // the app sees AAL2 immediately without waiting for a session refresh.
  refreshAAL: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentAAL, setCurrentAAL] = useState<AAL>(null);
  const [nextAAL, setNextAAL] = useState<AAL>(null);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error.message);
      return null;
    }
    return data as UserProfile;
  };

  const fetchAAL = async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      setCurrentAAL((data?.currentLevel as AAL) ?? null);
      setNextAAL((data?.nextLevel as AAL) ?? null);
    } catch (err) {
      Sentry.captureException(err);
      setCurrentAAL(null);
      setNextAAL(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let resolved = false;

    const finish = () => {
      if (cancelled || resolved) return;
      resolved = true;
      setLoading(false);
    };

    const apply = async (next: Session | null) => {
      if (cancelled) return;
      setSession(next);
      const u = next?.user ?? null;
      setUser(u);
      if (u) {
        const [p] = await Promise.all([fetchProfile(u.id), fetchAAL()]);
        if (cancelled) return;
        setProfile(p);
      } else {
        setProfile(null);
        setCurrentAAL(null);
        setNextAAL(null);
      }
      finish();
    };

    supabase.auth.getSession()
      .then(({ data }) => apply(data.session))
      .catch((err) => {
        Sentry.captureException(err);
        finish();
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, next) => {
      void apply(next);
    });

    const fallback = setTimeout(() => {
      if (cancelled || resolved) return;
      Sentry.captureMessage('AuthContext: session resolution timed out (8s)', 'warning');
      finish();
    }, 8000);

    return () => {
      cancelled = true;
      clearTimeout(fallback);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setCurrentAAL(null);
    setNextAAL(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  };

  return (
    <AuthContext.Provider value={{
      user, session, profile, loading,
      currentAAL, nextAAL,
      signIn, signOut, resetPassword, updatePassword, refreshAAL: fetchAAL,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
